import { z } from 'zod';

// Rolurile si permisiunile din firma — docs/23. Fiecare firma are rolurile ei (app.roles),
// pornite din ROLE_TEMPLATES si editabile cu bife din PERMISSIONS. Listele de aici sunt
// copiate in migrarea 20261108100000_roluri_cpv_judete.sql (marcajele contracts:*), iar
// test/sql-sync.test.ts verifica ca spun acelasi lucru. Regulile le aplica SQL-ul; UI-ul
// foloseste functiile de mai jos doar ca sa ascunda ce nu se poate.

export const PERMISSIONS = [
  'data.read', // cautare, fise, preturi (pe codurile CPV ale firmei)
  'data.all_counties', // toate judetele; fara ea, doar judetele alocate membrului
  'alerts.own', // alerte proprii
  'alerts.team', // alertele comune ale firmei
  'exports.create',
  'sales.view', // activitatea de vanzari a echipei si situatia agentilor
  'members.manage', // invita / scoate membri, le da roluri si judete
  'roles.manage', // creeaza si editeaza rolurile firmei
  'settings.manage', // datele firmei, numele din SEAP, codurile CPV
  'billing.manage', // abonament, facturi
  'org.owner', // predarea firmei (doar proprietarul)
] as const;
export const Permission = z.enum(PERMISSIONS);
export type Permission = z.infer<typeof Permission>;

export const PERMISSION_LABELS: Readonly<Record<Permission, string>> = {
  'data.read': 'Caută achiziții, produse și prețuri',
  'data.all_counties': 'Vede toate județele (altfel doar județele alocate)',
  'alerts.own': 'Alerte proprii',
  'alerts.team': 'Alertele comune ale firmei',
  'exports.create': 'Exportă în CSV',
  'sales.view': 'Activitatea de vânzări și situația agenților',
  'members.manage': 'Invită și administrează membrii',
  'roles.manage': 'Creează și editează rolurile',
  'settings.manage': 'Setările firmei: date, nume SEAP, coduri CPV',
  'billing.manage': 'Abonamentul și facturile',
  'org.owner': 'Predă firma (doar proprietarul)',
};

/** Cheia sablonului din app.roles.template. */
export const ROLE_TEMPLATE_KEYS = [
  'owner',
  'director',
  'administrator',
  'sales_manager',
  'sales_agent',
  'member',
  'viewer',
] as const;
export type RoleTemplateKey = (typeof ROLE_TEMPLATE_KEYS)[number];

export interface RoleTemplate {
  name: string;
  description: string;
  /** Proprietarul are toate permisiunile prin is_owner; lista lui ramane goala. */
  permissions: readonly Permission[];
}

export const ROLE_TEMPLATES: Readonly<Record<RoleTemplateKey, RoleTemplate>> = {
  owner: {
    name: 'Proprietar',
    description: 'Toate drepturile, inclusiv predarea firmei',
    permissions: [],
  },
  director: {
    name: 'Director',
    description: 'Vede tot: datele, vânzările, agenții',
    permissions: [
      'data.read',
      'data.all_counties',
      'alerts.own',
      'alerts.team',
      'exports.create',
      'sales.view',
    ],
  },
  administrator: {
    name: 'Administrator',
    description: 'Echipa, rolurile, setările și abonamentul',
    permissions: [
      'data.read',
      'data.all_counties',
      'alerts.own',
      'alerts.team',
      'exports.create',
      'sales.view',
      'members.manage',
      'roles.manage',
      'settings.manage',
      'billing.manage',
    ],
  },
  sales_manager: {
    name: 'Manager vânzări',
    description: 'Toată activitatea de vânzări și situația agenților',
    permissions: [
      'data.read',
      'data.all_counties',
      'alerts.own',
      'alerts.team',
      'exports.create',
      'sales.view',
    ],
  },
  sales_agent: {
    name: 'Agent vânzări',
    description: 'Doar județele alocate lui',
    permissions: ['data.read', 'alerts.own', 'exports.create'],
  },
  member: {
    name: 'Membru',
    description: 'Caută, are alerte proprii și exportă',
    permissions: ['data.read', 'data.all_counties', 'alerts.own', 'exports.create'],
  },
  viewer: {
    name: 'Vizualizare',
    description: 'Doar caută și citește',
    permissions: ['data.read', 'data.all_counties'],
  },
};

/** Un rol al firmei, cum il intoarce app.team / app.my_orgs. */
export interface OrgRole {
  id: string;
  name: string;
  is_owner: boolean;
  permissions: readonly Permission[];
}

/** Permisiunile unui rol (proprietarul le are pe toate). */
export function rolePermissions(role: Pick<OrgRole, 'is_owner' | 'permissions'>): Permission[] {
  return role.is_owner ? [...PERMISSIONS] : [...role.permissions];
}

/** Nimeni nu da mai mult decat are: toate permisiunile rolului sunt si ale actorului. */
export function withinPermissions(
  mine: readonly Permission[],
  role: Pick<OrgRole, 'is_owner' | 'permissions'>,
): boolean {
  return rolePermissions(role).every((p) => mine.includes(p));
}

/** Rolul se poate atribui: nu e proprietarul si nu da mai mult decat are actorul. */
export function canAssignRole(
  mine: readonly Permission[],
  role: Pick<OrgRole, 'is_owner' | 'permissions'>,
): boolean {
  return mine.includes('members.manage') && !role.is_owner && withinPermissions(mine, role);
}

/** Pe un membru il muta (rol, judete) sau il scoate cine are members.manage si macar
 *  drepturile lui; proprietarul nu se atinge, iar nimeni nu se muta singur. */
export function canManageMember(
  mine: readonly Permission[],
  member: Pick<OrgRole, 'is_owner' | 'permissions'>,
  self: boolean,
): boolean {
  return (
    !self && !member.is_owner && mine.includes('members.manage') && withinPermissions(mine, member)
  );
}

export const RoleInput = z.object({
  name: z.string().trim().min(2, 'Numele are cel puțin 2 litere').max(60),
  description: z.string().trim().max(300).optional(),
  permissions: z
    .array(Permission)
    .refine((p) => !p.includes('org.owner'), 'Predarea firmei rămâne doar a proprietarului'),
});
export type RoleInput = z.infer<typeof RoleInput>;

// „Functia” e doar informativa (ex. „Director achizitii”); drepturile vin exclusiv din rol.
export const JobTitle = z.string().trim().min(1).max(80);

/** Judetele, scrise ca in SEAP (fara diacritice), in ordinea din private.counties(). */
export const COUNTIES = [
  'Alba',
  'Arad',
  'Arges',
  'Bacau',
  'Bihor',
  'Bistrita-Nasaud',
  'Botosani',
  'Braila',
  'Brasov',
  'Bucuresti',
  'Buzau',
  'Calarasi',
  'Caras-Severin',
  'Cluj',
  'Constanta',
  'Covasna',
  'Dambovita',
  'Dolj',
  'Galati',
  'Giurgiu',
  'Gorj',
  'Harghita',
  'Hunedoara',
  'Ialomita',
  'Iasi',
  'Ilfov',
  'Maramures',
  'Mehedinti',
  'Mures',
  'Neamt',
  'Olt',
  'Prahova',
  'Salaj',
  'Satu Mare',
  'Sibiu',
  'Suceava',
  'Teleorman',
  'Timis',
  'Tulcea',
  'Valcea',
  'Vaslui',
  'Vrancea',
] as const;
export const County = z.enum(COUNTIES);
export type County = z.infer<typeof County>;
